import { GlAlert } from '@gitlab/ui';
import { sprintf, s__ } from '~/locale';
import { createAlert } from '~/alert';
import CrudComponent from '~/vue_shared/components/crud_component.vue';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import { getParameterByName } from '~/lib/utils/url_utility';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { FORM_TYPES, CHILD_ITEMS_ANCHOR, WORKITEM_TREE_SHOWCLOSED_LOCALSTORAGEKEY, WORKITEM_TREE_METADATA_LOCALSTORAGEKEY, WORK_ITEM_TYPE_NAME_EPIC, WIDGET_TYPE_HIERARCHY, DETAIL_VIEW_QUERY_PARAM_NAME, WORK_ITEM_TYPE_NAME_TICKET, WORK_ITEM_TREE_COLLAPSE_TRACKING_ACTION_COLLAPSED, WORK_ITEM_TREE_COLLAPSE_TRACKING_ACTION_EXPANDED, METADATA_KEYS, CONTEXTUAL_PANEL_KEYS } from '../../constants';
import { findHierarchyWidget, getDefaultHierarchyChildrenCount, saveToggleToLocalStorage, getToggleFromLocalStorage, getItems, trackCrudCollapse, getHiddenMetadataKeysFromLocalStorage, lowercaseWorkItemType } from '../../utils';
import getWorkItemTreeQuery from '../../graphql/work_item_tree.query.graphql';
import namespaceWorkItemTypesQuery from '../../graphql/namespace_work_item_types.query.graphql';
import WorkItemChildrenLoadMore from '../shared/work_item_children_load_more.vue';
import WorkItemMoreActions from '../shared/work_item_more_actions.vue';
import WorkItemToggleClosedItems from '../shared/work_item_toggle_closed_items.vue';
import WorkItemActionsSplitButton from './work_item_actions_split_button.vue';
import WorkItemLinksForm from './work_item_links_form.vue';
import WorkItemChildrenWrapper from './work_item_children_wrapper.vue';
import WorkItemRolledUpData from './work_item_rolled_up_data.vue';
import WorkItemRolledUpCount from './work_item_rolled_up_count.vue';
export default {
    name: 'WorkItemTree',
    components: {
        GlAlert,
        WorkItemActionsSplitButton,
        CrudComponent,
        WorkItemLinksForm,
        WorkItemChildrenWrapper,
        WorkItemChildrenLoadMore,
        WorkItemMoreActions,
        WorkItemRolledUpData,
        WorkItemRolledUpCount,
        WorkItemToggleClosedItems
    },
    mixins: [
        glFeatureFlagsMixin()
    ],
    inject: [
        'hasSubepicsFeature'
    ],
    provide () {
        return {
            preventRouterNav: !this.isDrawer
        };
    },
    props: {
        fullPath: {
            type: String,
            required: true
        },
        isGroup: {
            type: Boolean,
            required: false,
            default: false
        },
        workItemType: {
            type: String,
            required: true
        },
        parentWorkItemType: {
            type: String,
            required: false,
            default: ''
        },
        workItemId: {
            type: String,
            required: true
        },
        workItemIid: {
            type: String,
            required: false,
            default: null
        },
        confidential: {
            type: Boolean,
            required: false,
            default: false
        },
        canUpdate: {
            type: Boolean,
            required: false,
            default: false
        },
        canUpdateChildren: {
            type: Boolean,
            required: false,
            default: false
        },
        allowedChildTypes: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        isDrawer: {
            type: Boolean,
            required: false,
            default: false
        },
        activeChildItemId: {
            type: String,
            required: false,
            default: null
        },
        activePanel: {
            type: String,
            required: false,
            default: null
        },
        parentIteration: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        parentMilestone: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        contextualViewEnabled: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'add-child',
        'select-child'
    ],
    data () {
        return {
            error: undefined,
            formType: null,
            childType: null,
            widgetName: CHILD_ITEMS_ANCHOR,
            showClosed: true,
            fetchNextPageInProgress: false,
            workItem: {},
            disableContent: false,
            workItemTypes: [],
            hierarchyWidget: null,
            draggedItemType: null,
            hiddenMetadataKeys: getHiddenMetadataKeysFromLocalStorage(WORKITEM_TREE_METADATA_LOCALSTORAGEKEY, [
                METADATA_KEYS.PARENT
            ])
        };
    },
    apollo: {
        hierarchyWidget: {
            query: getWorkItemTreeQuery,
            variables () {
                return {
                    id: this.workItemId,
                    pageSize: getDefaultHierarchyChildrenCount(),
                    endCursor: '',
                    useWorkItemFeatures: Boolean(this.glFeatures?.workItemFeaturesField)
                };
            },
            skip () {
                return !this.workItemId;
            },
            update ({ workItem = {} }) {
                const { children } = findHierarchyWidget(workItem);
                this.workItem = workItem;
                return children || {};
            },
            error () {
                this.error = s__('WorkItems|An error occurred while fetching children');
            },
            result () {
                if (this.hasNextPage && this.children.length === 0) {
                    this.fetchNextPage();
                }
            }
        },
        workItemTypes: {
            query: namespaceWorkItemTypesQuery,
            variables () {
                return {
                    fullPath: this.fullPath
                };
            },
            update (data) {
                return data.namespace?.workItemTypes?.nodes;
            },
            skip () {
                return !this.canUpdate;
            }
        }
    },
    computed: {
        workItemHierarchy () {
            return findHierarchyWidget(this.workItem);
        },
        rolledUpCountsByType () {
            return this.workItemHierarchy?.rolledUpCountsByType || [];
        },
        depthLimitReachedByType () {
            return this.workItemHierarchy?.depthLimitReachedByType || [];
        },
        childrenIds () {
            return this.children.map((c)=>c.id);
        },
        addItemsActions () {
            let childTypes = this.allowedChildTypes;
            if (!this.hasSubepicsFeature) {
                childTypes = childTypes.filter((type)=>type.name !== WORK_ITEM_TYPE_NAME_EPIC);
            }
            const reorderedChildTypes = childTypes.slice().sort((a, b)=>a.id.localeCompare(b.id));
            return reorderedChildTypes.map((type)=>{
                const depthLimitByType = this.depthLimitReachedByType?.find((item)=>item.workItemType?.id === type.id) || {};
                return {
                    name: type.name,
                    atDepthLimit: depthLimitByType.depthLimitReached,
                    items: this.genericActionItems(type).map((item)=>({
                            text: item.title,
                            action: item.action,
                            extraAttrs: {
                                disabled: depthLimitByType.depthLimitReached
                            }
                        }))
                };
            });
        },
        children () {
            return this.hierarchyWidget?.nodes || [];
        },
        hasIndirectChildren () {
            return this.children.map((child)=>findHierarchyWidget(child) || {}).some((hierarchy)=>hierarchy.hasChildren);
        },
        isLoadingChildren () {
            return this.$apollo.queries.hierarchyWidget.loading;
        },
        showEmptyMessage () {
            return this.children.length === 0 && !this.isLoadingChildren && !this.error;
        },
        pageInfo () {
            return this.hierarchyWidget?.pageInfo;
        },
        endCursor () {
            return this.pageInfo?.endCursor || '';
        },
        hasNextPage () {
            return this.pageInfo?.hasNextPage;
        },
        workItemNamespaceName () {
            return this.workItem?.namespace?.fullName;
        },
        allowedChildrenByType () {
            return this.workItemTypes.reduce((acc, type)=>{
                const definition = type.widgetDefinitions?.find((widgetDefinition)=>widgetDefinition.type === WIDGET_TYPE_HIERARCHY);
                if (definition?.allowedChildTypes?.nodes?.length > 0) {
                    acc[type.name] = definition.allowedChildTypes.nodes.map((a)=>a.name);
                }
                return acc;
            }, {});
        },
        displayableChildrenCount () {
            const filterClosed = getItems(this.showClosed);
            return filterClosed(this.children)?.length;
        },
        hasAllChildItemsHidden () {
            return this.displayableChildrenCount === 0;
        },
        showClosedItemsButton () {
            if (this.hasNextPage) return false;
            return !this.showClosed && this.children?.length > this.displayableChildrenCount;
        },
        closedChildrenCount () {
            return Math.max(0, this.children.length - this.displayableChildrenCount);
        },
        toggleClosedItemsClasses () {
            return {
                '!gl-pl-6': this.hasIndirectChildren,
                '!gl-px-3 gl-pb-3 gl-pt-2': !this.hasAllChildItemsHidden
            };
        },
        shouldShowTreeWidget () {
            return this.children.length > 0 || this.canUpdateChildren;
        }
    },
    watch: {
        'workItem.id': {
            // we only want to open the drawer on initial widget load and not from drawer widget
            handler (newValue, oldValue) {
                if (!oldValue && newValue && !this.isDrawer) {
                    this.checkDrawerParams();
                }
            }
        }
    },
    created () {
        window.addEventListener('popstate', this.checkDrawerParams);
    },
    beforeDestroy () {
        window.removeEventListener('popstate', this.checkDrawerParams);
    },
    mounted () {
        this.showClosed = getToggleFromLocalStorage(WORKITEM_TREE_SHOWCLOSED_LOCALSTORAGEKEY);
    },
    methods: {
        genericActionItems (type) {
            const workItemName = lowercaseWorkItemType(type.name);
            return [
                ...type.name === WORK_ITEM_TYPE_NAME_TICKET ? [] : [
                    {
                        title: sprintf(s__('WorkItem|New %{workItemName}'), {
                            workItemName
                        }),
                        action: ()=>this.showAddForm(FORM_TYPES.create, type)
                    }
                ],
                {
                    title: sprintf(s__('WorkItem|Existing %{workItemName}'), {
                        workItemName
                    }),
                    action: ()=>this.showAddForm(FORM_TYPES.add, type)
                }
            ];
        },
        showAddForm (formType, childType) {
            this.$refs.workItemTree.showForm();
            this.formType = formType;
            this.childType = childType;
            this.$nextTick(()=>{
                this.$refs.wiLinksForm.$refs.wiTitleInput?.$el.focus();
            });
        },
        hideAddForm () {
            this.$refs.workItemTree.hideForm();
            this.formType = null;
        },
        selectChild ({ event, child }) {
            this.$emit('select-child', {
                event,
                child
            });
        },
        toggleShowClosed () {
            this.showClosed = !this.showClosed;
            saveToggleToLocalStorage(WORKITEM_TREE_SHOWCLOSED_LOCALSTORAGEKEY, this.showClosed);
        },
        handleUpdateHiddenMetadataKeys (hiddenKeys) {
            this.hiddenMetadataKeys = [
                ...hiddenKeys
            ];
        },
        async fetchNextPage () {
            if (this.hasNextPage && !this.fetchNextPageInProgress) {
                this.fetchNextPageInProgress = true;
                try {
                    await this.$apollo.queries.hierarchyWidget.fetchMore({
                        variables: {
                            endCursor: this.endCursor
                        }
                    });
                } catch (error) {
                    createAlert({
                        message: s__('Hierarchy|Something went wrong while fetching children.'),
                        captureError: true,
                        error
                    });
                } finally{
                    this.fetchNextPageInProgress = false;
                }
            }
        },
        checkDrawerParams () {
            const queryParam = getParameterByName(DETAIL_VIEW_QUERY_PARAM_NAME);
            if (!queryParam) {
                this.$emit('select-child', {
                    child: null
                });
                return;
            }
            // `show` is shared with the contextual panels, which store a plain panel key rather than
            // an encoded child reference. Those keys are expected; anything else that fails to
            // decode is a real error.
            let params;
            try {
                params = JSON.parse(atob(queryParam));
            } catch (error) {
                if (!CONTEXTUAL_PANEL_KEYS.includes(queryParam)) {
                    Sentry.captureException(error);
                }
                return;
            }
            if (params.id) {
                const child = this.children.find((i)=>getIdFromGraphQLId(i.id) === params.id);
                if (child) {
                    this.$emit('select-child', {
                        child
                    });
                }
            }
        },
        handleCrudCollapsed (collapsed) {
            trackCrudCollapse(collapsed ? WORK_ITEM_TREE_COLLAPSE_TRACKING_ACTION_COLLAPSED : WORK_ITEM_TREE_COLLAPSE_TRACKING_ACTION_EXPANDED);
        }
    },
    i18n: {
        noChildItemsOpen: s__('WorkItem|No child items are currently open.')
    },
    WORKITEM_TREE_METADATA_LOCALSTORAGEKEY,
    METADATA_KEYS,
    defaultHiddenMetadataKeys: [
        METADATA_KEYS.PARENT
    ]
};
